import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Paperclip, Search, X } from '@digital-twins/design-system/icons'
import { toast } from 'sonner'
import { mutate as mutateGlobalCache } from 'swr'
import {
  AssistantEffort,
  MAX_CONTEXT_MATERIAL_FILE_BYTES,
  MAX_CONTEXT_MATERIAL_FILE_MB,
  WorkflowTemplate,
} from '@digital-twins/shared-types'
import { ChatComposer } from '@/components/chat/ChatComposer'
import { ContextRing } from '@/components/composer/ContextRing'
import { EffortToggle } from '@/components/composer/EffortToggle'
import { ScopeChip } from '@/components/composer/ScopeChip'
import { PageContent } from '@/components/layout/PageContent'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageLayout } from '@/components/layout/PageLayout'
import {
  SIDEBAR_THREADS_KEY,
  mapSidebarThreadsCache,
  upsertSidebarThread,
} from '@/components/layout/useSidebarThreads'
import { LobbyEmptyState } from '@/components/shared/LobbyEmptyState'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { useAssistantPlatformEnabled } from '@/hooks/useAssistantPlatformEnabled'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useAuth } from '@/lib/config/auth-provider'
import { PYLON_CONTACT_SUPPORT_URL } from '@/lib/constants/pylon'
import { cn } from '@/lib/utils'
import { templateSurface } from '@/lib/workflows/templateSurface'
import {
  knowledgeApi,
  knowledgeThreadsApi,
  KnowledgeExamplePrompt,
  KnowledgeThreadListItem,
  KnowledgeThreadView,
} from '@/lib/api'
import { defaultContentLanguageFromUi } from '@/i18n'
import { ROUTES } from '@/routes/routes'
import { WorkflowMenu } from '@/pages/HomeLabPage/WorkflowMenu'
import { ThreadCommandMenu, ThreadCommandSeed } from '@/pages/ThreadDetailPage/ThreadCommandMenu'
import {
  clearRememberedAudienceStorage,
  loadRememberedAudience,
  RememberedAudience,
  writeArmedWorkflowState,
  writeRememberedAudience,
} from '@/pages/ThreadDetailPage/useArmedWorkflowState'
import {
  matchesTypedCommand,
  REPORT_COMMAND_NAME,
  REPORT_COMMAND_SLUG,
  useThreadCommands,
} from '@/pages/ThreadDetailPage/useThreadCommands'
import {
  hasUnfillableRequiredInput,
  mergeStartInputs,
} from '@/pages/WorkflowsPage/useStartWorkflowRun'
import { useWorkflowTemplate } from '@/pages/WorkflowsPage/useWorkflowTemplate'
import { useWorkflowTemplates } from '@/pages/WorkflowsPage/useWorkflowTemplates'
import {
  KnowledgeScopePicker,
  KnowledgeScopeSelection,
} from './KnowledgeScopePicker'
import { ExamplePromptChips } from './ExamplePromptChips'
import { ThreadCard } from './ThreadCard'
import { askScopeFromParams, persistableAskScope } from './askScope'
import { scopeChipLabel } from './threadHelpers'

const EMPTY_SCOPE: KnowledgeScopeSelection = {
  web: true,
  library: true,
  mode: 'all',
  documentTokens: [],
  collectionTokens: [],
}

const ATTACH_ACCEPT =
  '.txt,.csv,.html,.htm,.json,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx'

/**
 * Client-side match. `knowledgeThreadsApi.list()` takes no arguments and returns
 * the whole list, so there is no search endpoint to defer to: this narrows what
 * is already loaded, over the two fields a row actually shows.
 */
function threadMatchesQuery(
  thread: KnowledgeThreadListItem,
  needle: string,
): boolean {
  const haystack = `${thread.title || ''} ${thread.lastMessagePreview || ''}`
  return haystack.toLowerCase().includes(needle)
}

export default function ThreadsPage() {
  const { t, i18n } = useTranslation('app')
  const navigate = useNavigate()
  const contentLanguage = defaultContentLanguageFromUi(i18n.language)
  const [searchParams] = useSearchParams()
  const assistantPlatformEnabled = useAssistantPlatformEnabled()
  /* The remembered audience is scoped to the ORGANIZATION, not the browser tab
     (thread-audience-remember-and-avatar-fallback, Decision 1), so this
     landing door needs the org id to read the same bucket `ThreadDetailPage`
     writes (plan 2026-09-14-twin-test-bound-audience, C1). */
  const { org: currentOrg } = useCurrentOrg()
  const { isLoading: isAuthLoading } = useAuth()
  // Unresolved profile reports the flag as false — hold the skeleton until auth
  // resolves, then lock only when the flag is actually off.
  const canLoadThreads = !isAuthLoading && assistantPlatformEnabled
  const isThreadsLocked = !isAuthLoading && !assistantPlatformEnabled
  const [threads, setThreads] = useState<Array<KnowledgeThreadListItem>>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [attaching, setAttaching] = useState(false)
  const [scope, setScope] = useState<KnowledgeScopeSelection>(EMPTY_SCOPE)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [effort, setEffort] = useState<AssistantEffort>('medium')
  const [armedWorkflowSlug, setArmedWorkflowSlug] = useState<string | null>(null)
  /**
   * The confirm screen's own answers (D1 hand-off): `ThreadCommandMenu.onArm`
   * hands over `/test`'s picked audience the same way `startCommand` would
   * have posted it. Empty for a lobby workflow armed through `WorkflowMenu`,
   * which has no confirm screen of its own.
   */
  const [armedCommandInputs, setArmedCommandInputs] = useState<
    Record<string, string | null>
  >({})
  const [armedCommandAudienceName, setArmedCommandAudienceName] = useState<
    string | null
  >(null)
  /** Busy while D1's hand-off mints the thread the armed run will start in. */
  const [isMintingThread, setIsMintingThread] = useState(false)
  const [commandSeed, setCommandSeed] = useState<ThreadCommandSeed | null>(null)
  /**
   * The org's remembered audience (C1). This landing door has no composer
   * chip of its own (it has no armed composer header the way `HomeLabStage`
   * does), so the only thing this state does is let "Remember this audience"
   * actually persist and prefill here, per D5. Lazy-initialized from the org
   * id this render already has; the effect below corrects the namespace once
   * `useCurrentOrg`'s async resolution lands.
   */
  const [rememberedAudience, setRememberedAudience] =
    useState<RememberedAudience | null>(() =>
      loadRememberedAudience(currentOrg?.id ?? null),
    )
  const { templates: workflowTemplates } = useWorkflowTemplates({
    enabled: assistantPlatformEnabled,
  })
  const { template: armedWorkflow } = useWorkflowTemplate(
    armedWorkflowSlug ?? undefined,
    { enabled: assistantPlatformEnabled },
  )
  /* D5: off-thread (no thread minted yet) only ever hands off `/test` — this
     feeds the composer's own typed `/` autocomplete and typed-command
     matcher below, not the `ThreadCommandMenu` dropdown mounted in the footer. */
  const { commands: threadCommands } = useThreadCommands(assistantPlatformEnabled, {
    surface: 'off-thread',
  })
  const composerCommands = armedWorkflowSlug
    ? undefined
    : threadCommands.map((entry) => ({
        command: entry.command,
        label: entry.label,
        description: entry.summary,
      }))
  const [searchQuery, setSearchQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  /** Server-side search results (uncollapsed). Null when not searching. */
  const [searchResults, setSearchResults] = useState<Array<KnowledgeThreadListItem> | null>(null)
  const [composerSeed, setComposerSeed] = useState<{
    text: string
    nonce: number
  } | null>(null)
  const [sourcePromptId, setSourcePromptId] = useState<string | null>(null)
  const attachInputRef = useRef<HTMLInputElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  /**
   * Latches the in-flight/completed `create()` call for a given `?q=` so a
   * StrictMode double-invoke (or any re-render that leaves `q` unchanged)
   * cannot mint a second server thread. Keyed on the query text itself: a
   * second effect invocation for the SAME `q` reuses the first call's promise
   * instead of firing another `create()`, but still awaits it and still
   * navigates once it resolves — only the network call is deduplicated, not
   * the completion.
   */
  const pendingCreateRef = useRef<{ query: string; promise: Promise<KnowledgeThreadView> } | null>(
    null,
  )

  useEffect(() => {
    if (isAuthLoading) {
      return
    }
    if (!assistantPlatformEnabled) {
      setLoading(false)
      return
    }

    let cancelled = false
    void (async () => {
      setLoading(true)
      try {
        const list = await knowledgeThreadsApi.list()
        if (cancelled) return
        setThreads(list)
      } catch (error: unknown) {
        const message =
          (error as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? t('threads.errors.loadFailed')
        toast.error(message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // `t` is only the catch-toast fallback. Listing it would refetch the list
    // on every UI-language change, which this effect never did (deps were `[]`
    // before the lock gate). Auth and the flag are the only reasons to re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- catch toast only; do not refetch on language change
  }, [isAuthLoading, assistantPlatformEnabled])

  // A question arriving via ?q= (from the Library's search-or-ask box, or any
  // other deep link) should land straight inside a new thread with that
  // question already asked — not sit in the lobby composer waiting to be
  // resent. `doc` alone (no `q`) just scopes the composer to that document;
  // `collection` does the same for a knowledge collection (a social tracker's)
  // and keeps the web out, so the answer reads only that tracker's posts.
  // `source=social` limits the ask to social digests, and `window` (e.g. 7d)
  // to posts published in that window, fixed to dates ending now. A Topic
  // Ask (`topic`, plus `tracker` / `post` / filters) seeds an Auto scope with
  // the web on and a `socialFocus` the thread keeps (plan 2026-11-01 D0).
  useEffect(() => {
    const q = searchParams.get('q')?.trim()
    const askScope = askScopeFromParams(searchParams)
    const scopeSeed: KnowledgeScopeSelection = askScope ?? EMPTY_SCOPE

    if (askScope && canLoadThreads) {
      setScope(scopeSeed)
    }
    if (!q) {
      pendingCreateRef.current = null
      return
    }
    // Skip create/navigate while auth is unresolved or Assistant is off —
    // gating the list effect alone leaves /threads?q= live.
    if (!canLoadThreads) return

    let cancelled = false
    setCreating(true)

    // Reuse an already-started `create()` for this exact `q` instead of firing
    // a second one: StrictMode invokes this effect twice in dev (mount, undo,
    // mount again) and, separately, any re-render that leaves `q` unchanged
    // would otherwise mint a second server thread before the first call ever
    // resolves — the earlier `cancelled` guard only skips the FOLLOW-UP work
    // (navigate/toast) for a stale invocation, it never stopped the network
    // call itself from firing twice.
    const pending = pendingCreateRef.current
    const createPromise =
      pending && pending.query === q
        ? pending.promise
        : knowledgeThreadsApi.create({
            scope: persistableAskScope(scopeSeed),
            language: contentLanguage,
          })
    pendingCreateRef.current = { query: q, promise: createPromise }

    void (async () => {
      try {
        const thread = await createPromise
        if (cancelled) return
        upsertSidebarThread(thread)
        navigate(ROUTES.thread(thread.token), {
          replace: true,
          state: { pendingMessage: q, scope: scopeSeed },
        })
      } catch (error: unknown) {
        // Clear the latch on rejection so a re-run of this effect for the SAME
        // `q` (e.g. `canLoadThreads` or `contentLanguage` flipping while still
        // on `/threads?q=…`) fires a fresh `create()` instead of re-awaiting
        // the dead promise forever (which would just re-toast and never retry).
        if (pendingCreateRef.current?.query === q) {
          pendingCreateRef.current = null
        }
        if (cancelled) return
        const message =
          (error as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? t('threads.errors.startFailed')
        toast.error(message)
        setComposerSeed({ text: q, nonce: Date.now() })
      } finally {
        if (!cancelled) setCreating(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // `t` is only the catch-toast fallback; listing it would refetch/re-fire
    // this effect on every UI-language change, which it never did before.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- catch toast only; do not re-fire on language change
  }, [searchParams, navigate, contentLanguage, canLoadThreads])

  useEffect(() => {
    if (searchOpen) {
      requestAnimationFrame(() => {
        searchInputRef.current?.focus()
      })
    }
  }, [searchOpen])

  /* Corrects the remembered-audience namespace once `useCurrentOrg` resolves
     past its initial `null` — the lazy initializer above already read the
     `null` (unscoped) bucket on first paint. Mirrors the same effect in
     `HomeLabStage.tsx` and `useArmedWorkflowState`'s own `hydratedOrgIdRef`
     effect for the in-thread door. */
  useEffect(() => {
    setRememberedAudience(loadRememberedAudience(currentOrg?.id ?? null))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id])

  const trimmedQuery = searchQuery.trim()

  // Step 9: when a query is present, fetch server-side uncollapsed results so
  // every individual day is findable, not just the collapsed newest. When the
  // query is cleared, revert to the collapsed list already in `threads`.
  useEffect(() => {
    const q = trimmedQuery
    if (!q || !assistantPlatformEnabled) {
      setSearchResults(null)
      return
    }
    let cancelled = false
    void knowledgeThreadsApi.list({ q }).then((results) => {
      if (!cancelled) setSearchResults(results)
    }).catch(() => {
      if (!cancelled) setSearchResults([])
    })
    return () => {
      cancelled = true
    }
  }, [trimmedQuery, assistantPlatformEnabled])

  // While the server search is in-flight, fall back to client-side filtering
  // on the already-loaded `threads` so the user sees instant feedback rather
  // than an empty list. Once the server responds with the uncollapsed results
  // (which can surface older per-day rows that collapsed away), we swap in
  // those. On clear, revert to the full collapsed list.
  const visibleThreads = (searchResults ?? (
    trimmedQuery
      ? threads.filter((t) => threadMatchesQuery(t, trimmedQuery.toLowerCase()))
      : threads
  )).slice().sort((left, right) => {
    const leftPinned = left.pinned === true
    const rightPinned = right.pinned === true
    if (leftPinned !== rightPinned) {
      return leftPinned ? -1 : 1
    }
    return 0
  })

  async function handleSend(text: string) {
    const trimmed = text.trim()
    if (!trimmed || creating || isMintingThread) return
    if (armedWorkflow) {
      void handleArmedWorkflowSubmit(armedWorkflow, trimmed)
      return
    }
    setCreating(true)
    try {
      const thread = await knowledgeThreadsApi.create({
        scope: persistableAskScope(scope),
        language: contentLanguage,
        ...(sourcePromptId ? { sourcePromptId } : {}),
      })
      upsertSidebarThread(thread)
      navigate(ROUTES.thread(thread.token), {
        state: {
          pendingMessage: trimmed,
          scope,
          pendingEffort: effort,
          ...(sourcePromptId ? { sourcePromptId } : {}),
        },
      })
      setSourcePromptId(null)
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? t('threads.errors.startFailed')
      toast.error(message)
    } finally {
      setCreating(false)
    }
  }

  function handleExamplePromptSelect(prompt: KnowledgeExamplePrompt) {
    setComposerSeed({ text: prompt.text, nonce: Date.now() })
    setSourcePromptId(prompt.promptId)
  }

  /**
   * `ThreadCommandMenu`'s arming door (today, `/test`'s confirm screen).
   * Keeps the FULL answer — not just the slug — so the D1 hand-off in
   * `handleArmedWorkflowSubmit` can seed `ArmedWorkflowState` with exactly
   * what the confirm screen collected.
   */
  function handleArmCommand(
    template: WorkflowTemplate,
    inputs: Record<string, string | null>,
    armDetails?: { audienceName: string | null },
  ) {
    setArmedWorkflowSlug(template.slug)
    setArmedCommandInputs(inputs)
    setArmedCommandAudienceName(armDetails?.audienceName ?? null)
    /* The words typed around a slash command travel as the template's text
       input and stay in the composer while the card is open; the seed is the
       same text, so this only matters for a seed that carried its own ask
       (same hand-off as `ThreadDetailPage`). */
    const askKey = template.inputs.find((spec) => spec.type === 'text')?.key
    const seededAsk = askKey ? inputs[askKey] : null
    if (typeof seededAsk === 'string' && seededAsk.trim().length > 0) {
      setComposerSeed({ text: seededAsk.trim(), nonce: Date.now() })
    }
  }

  /**
   * Writes the org-scoped remembered audience AND updates local state in the
   * same call, so a later card open on this page prefills without a reload
   * (C1). Passed to `ThreadCommandMenu` as `onRememberAudience`.
   */
  function handleRememberAudience(audience: RememberedAudience) {
    writeRememberedAudience(currentOrg?.id ?? null, audience)
    setRememberedAudience(audience)
  }

  /**
   * Forgets the org-scoped remembered audience (C3's only forget path).
   * Passed to `ThreadCommandMenu` as `onForgetRememberedAudience`.
   */
  function handleForgetRememberedAudience() {
    clearRememberedAudienceStorage(currentOrg?.id ?? null)
    setRememberedAudience(null)
  }

  /** `WorkflowMenu`'s door: a lobby slug and nothing else — no confirm screen. */
  function handleSelectLobbyWorkflow(slug: string | null) {
    setArmedWorkflowSlug(slug)
    setArmedCommandInputs({})
    setArmedCommandAudienceName(null)
  }

  /**
   * D1 hand-off for an armed workflow: mint the thread, seed
   * `ArmedWorkflowState` WITH the template record, and navigate — never
   * `workflowRunsApi.create` from here. `ThreadDetailPage`'s own pending
   * effect is what starts the run (or opens intake for `/test`), so a 409 or
   * any other start error now toasts there, not on this page.
   *
   * Refuses BEFORE minting, with the exact toast `useStartWorkflowRun` would
   * have shown, using its own exported `hasUnfillableRequiredInput` /
   * `mergeStartInputs` so this page refuses on the identical predicate.
   */
  async function handleArmedWorkflowSubmit(
    template: WorkflowTemplate,
    message: string,
  ) {
    if (isMintingThread) {
      return
    }
    const mergedInputs = mergeStartInputs(template, message, armedCommandInputs)
    if (hasUnfillableRequiredInput(template, mergedInputs)) {
      toast.error(
        t('workflows.run.needsSetup', { name: template.displayName }),
        {
          description: t('workflows.run.openToFillRest'),
          action: {
            label: t('workflows.run.openWorkflow'),
            onClick: () => navigate(ROUTES.workflow(template.slug)),
          },
        },
      )
      return
    }

    setIsMintingThread(true)
    try {
      const thread = await knowledgeThreadsApi.create({
        scope: persistableAskScope(scope),
        language: contentLanguage,
      })
      upsertSidebarThread(thread)
      writeArmedWorkflowState(thread.token, {
        slug: template.slug,
        template,
        inputs: armedCommandInputs,
        audienceName: armedCommandAudienceName,
        intakeDirection: null,
        latchedStartKey: null,
      })
      navigate(ROUTES.thread(thread.token), {
        state: {
          pendingMessage: message,
          pendingEffort: effort,
          scope,
        },
      })
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ?? t('threads.errors.startFailed')
      toast.error(message)
    } finally {
      setIsMintingThread(false)
    }
  }

  /**
   * `/report` has no confirm card and no template, so typed here it goes straight
   * to a new thread, armed. The thread page opens the scoping beat from there.
   */
  async function handleReportCommand(rest: string) {
    if (isMintingThread) {
      return
    }
    setIsMintingThread(true)
    try {
      const thread = await knowledgeThreadsApi.create({
        scope: persistableAskScope(scope),
        language: contentLanguage,
      })
      upsertSidebarThread(thread)
      writeArmedWorkflowState(thread.token, {
        slug: REPORT_COMMAND_SLUG,
        template: null,
        inputs: {},
        audienceName: null,
        intakeDirection: null,
        latchedStartKey: null,
      })
      navigate(ROUTES.thread(thread.token), {
        state: {
          pendingMessage: rest,
          pendingIntakeOpen: true,
          pendingEffort: effort,
          scope,
        },
      })
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ?? t('threads.errors.startFailed')
      toast.error(message)
      /* The composer already dropped the token: a retry must not send a plain ask. */
      setComposerSeed({ text: `${REPORT_COMMAND_NAME} ${rest}`.trim(), nonce: Date.now() })
    } finally {
      setIsMintingThread(false)
    }
  }

  function handleTypedCommand(command: string, rest: string): boolean {
    const typed = command.toLowerCase()
    if (typed === REPORT_COMMAND_NAME && assistantPlatformEnabled) {
      void handleReportCommand(rest)
      return true
    }
    const match = threadCommands.find(
      (entry) => matchesTypedCommand(entry, typed),
    )
    if (!match) {
      return false
    }

    setCommandSeed((current) => ({
      command: match.command,
      rest,
      nonce: (current?.nonce ?? 0) + 1,
    }))
    return true
  }

  async function handleAttachFiles(fileList: FileList | null) {
    if (!fileList?.length || attaching) return
    const files = Array.from(fileList)
    const oversized = files.filter((f) => f.size > MAX_CONTEXT_MATERIAL_FILE_BYTES)
    if (oversized.length > 0) {
      toast.error(
        oversized.length === 1
          ? t('threads.attach.singleOversize', {
              name: oversized[0].name,
              mb: MAX_CONTEXT_MATERIAL_FILE_MB,
            })
          : t('threads.attach.multipleOversize', {
              count: oversized.length,
              mb: MAX_CONTEXT_MATERIAL_FILE_MB,
            }),
      )
      if (attachInputRef.current) attachInputRef.current.value = ''
      return
    }

    setAttaching(true)
    try {
      const uploaded =
        files.length === 1
          ? [await knowledgeApi.uploadDocument(files[0])]
          : (await knowledgeApi.bulkUpload(files)).documents

      setScope((prev) => {
        if (prev.mode !== 'selected') return prev
        const nextTokens = [
          ...prev.documentTokens,
          ...uploaded.map((d) => d.token).filter(Boolean),
        ]
        return {
          ...prev,
          documentTokens: Array.from(new Set(nextTokens)),
        }
      })

      toast.success(
        uploaded.length === 1
          ? t('threads.attach.toastAddedOne', {
              name: uploaded[0].originalFilename || t('threads.attach.documentFallback'),
            })
          : t('threads.attach.toastAddedMany', { count: uploaded.length }),
        { description: t('threads.attach.toastIndexingDescription') },
      )
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? t('threads.errors.attachFailed')
      toast.error(message)
    } finally {
      setAttaching(false)
      if (attachInputRef.current) attachInputRef.current.value = ''
    }
  }

  /**
   * Patches this page's own local list AND the sidebar rail's shared SWR
   * cache (`SIDEBAR_THREADS_KEY`) directly, no refetch: the rail is mounted
   * alongside this page on desktop, so without this it would keep showing
   * the pre-rename title until its own next fetch.
   */
  async function handleRenameThread(token: string, title: string) {
    try {
      const updated = await knowledgeThreadsApi.update(token, { title })
      setThreads((prev) =>
        prev.map((t) => (t.token === token ? { ...t, title: updated.title } : t)),
      )
      void mutateGlobalCache(
        SIDEBAR_THREADS_KEY,
        (current) =>
          mapSidebarThreadsCache(current, (threads) =>
            threads.map((t) => (t.token === token ? { ...t, title: updated.title } : t)),
          ),
        { revalidate: false },
      )
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? t('threads.errors.renameFailed')
      toast.error(message)
    }
  }

  async function handlePinThread(token: string, pinned: boolean) {
    try {
      const updated = await knowledgeThreadsApi.update(token, { pinned })
      const nextPinned = updated.pinned ?? pinned
      setThreads((prev) =>
        prev.map((t) => (t.token === token ? { ...t, pinned: nextPinned } : t)),
      )
      void mutateGlobalCache(
        SIDEBAR_THREADS_KEY,
        (current) =>
          mapSidebarThreadsCache(current, (threads) =>
            threads.map((t) => (t.token === token ? { ...t, pinned: nextPinned } : t)),
          ),
        { revalidate: false },
      )
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ??
        t(pinned ? 'threads.errors.pinFailed' : 'threads.errors.unpinFailed')
      toast.error(message)
    }
  }

  async function handleDeleteThread(token: string) {
    try {
      await knowledgeThreadsApi.delete(token)
      setThreads((prev) => prev.filter((t) => t.token !== token))
      void mutateGlobalCache(
        SIDEBAR_THREADS_KEY,
        (current) =>
          mapSidebarThreadsCache(current, (threads) => threads.filter((t) => t.token !== token)),
        { revalidate: false },
      )
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? t('threads.errors.deleteFailed')
      toast.error(message)
    }
  }

  const chipLabel = scopeChipLabel(scope)
  const searchActive = searchOpen || searchQuery.length > 0
  // Hidden entirely with nothing to search: an empty lobby offers the composer, not a filter.
  const showSearch =
    !isThreadsLocked &&
    (threads.length > 0 || trimmedQuery.length > 0 || searchOpen)
  const showEmptyState = !loading && !isThreadsLocked && threads.length === 0
  const showNoResults =
    !loading &&
    !isThreadsLocked &&
    trimmedQuery.length > 0 &&
    visibleThreads.length === 0
  const isComposerDisabled =
    !canLoadThreads || creating || attaching || isMintingThread

  return (
    <div className="min-h-screen bg-background">
      <PageLayout className="max-w-none p-8 lg:p-8" data-testid="threads-page">
        <PageHeader
          title={(
            <Heading level={1} className="text-2xl md:text-4xl">
              {t('threads.heading')}
            </Heading>
          )}
          description={t('threads.description')}
          action={showSearch ? (
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <div
                className={cn(
                  'flex h-10 items-center overflow-hidden rounded-lg border-[1.5px] border-cta bg-background/45 transition-all duration-200',
                  searchActive ? 'w-[320px] px-2' : 'w-10 px-0',
                )}
              >
                <button
                  type="button"
                  aria-label={t('threads.search.toggleAria')}
                  data-testid="threads-search-toggle"
                  onClick={() => setSearchOpen(true)}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center text-cta"
                >
                  <Search className="h-4 w-4" />
                </button>
                <Input
                  ref={searchInputRef}
                  type="text"
                  placeholder={t('threads.search.placeholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="threads-search-input"
                  className={cn(
                    'h-10 flex-1 border-0 bg-transparent px-0 text-sm text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0',
                    !searchActive && 'pointer-events-none opacity-0',
                  )}
                />
                <button
                  type="button"
                  aria-label={t('threads.search.clearAria')}
                  data-testid="threads-search-clear"
                  onClick={() => {
                    setSearchQuery('')
                    setSearchOpen(false)
                  }}
                  className={cn(
                    'inline-flex h-10 w-8 shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-foreground',
                    !searchActive && 'pointer-events-none opacity-0',
                  )}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : null}
        />

        {/* Hidden while locked, not merely disabled: `ChatComposer`'s `disabled` prop
            (without `disabledMessage`) renders the send button with a permanent
            `animate-spin` Loader2, a treatment meant for transient states
            (creating/attaching/isMintingThread), not this stable, indefinite lock. */}
        {isThreadsLocked ? null : (
        <div data-testid="threads-composer-wrap">
          <input
            ref={attachInputRef}
            type="file"
            className="hidden"
            accept={ATTACH_ACCEPT}
            multiple
            data-testid="threads-attach-input"
            onChange={(e) => void handleAttachFiles(e.target.files)}
          />
          <ChatComposer
            onSend={(text) => void handleSend(text)}
            disabled={isComposerDisabled}
            placeholder={t('threads.composerPlaceholder')}
            seed={composerSeed}
            commands={composerCommands}
            onCommand={armedWorkflowSlug ? undefined : handleTypedCommand}
            commandAppearance="internal"
            commandPreviewPlacement="above"
            dataTestId="threads-composer"
            textareaDataTestId="threads-composer-input"
            /* Same hairline as Home Lab: a drop shadow on this landing box
               is the loudest thing on the page. */
            flat
            footerSlot={
              <div className="flex flex-wrap items-center gap-2">
                {assistantPlatformEnabled ? (
                  <ThreadCommandMenu
                    threadToken={null}
                    activeRun={null}
                    appearance="internal"
                    placement="below"
                    /* D1: no `ensureThread` off-thread any more — an armed command hands off
                       through `handleArmedWorkflowSubmit` instead of starting on the spot, so
                       there is no thread for a start-now command to mint here. `onRunStarted`
                       stays required by the prop type but can never fire without `ensureThread`
                       (`startCommand` bails before reaching it): a non-arming command
                       (`/report`, `/slack`) picked from this menu off-thread is a known Step 1
                       gap, tracked for the follow-up that teaches this menu the same hand-off. */
                    onRunStarted={() => {}}
                    onArm={handleArmCommand}
                    rememberedAudience={rememberedAudience}
                    onRememberAudience={handleRememberAudience}
                    onForgetRememberedAudience={handleForgetRememberedAudience}
                    isSelected={
                      armedWorkflowSlug
                        ? templateSurface(
                            workflowTemplates.find((template) => template.slug === armedWorkflowSlug) ?? {
                              slug: armedWorkflowSlug,
                            },
                          ) === 'thread'
                        : false
                    }
                    seed={commandSeed}
                  />
                ) : null}
                <ScopeChip
                  label={chipLabel}
                  onClick={() => setPickerOpen(true)}
                  appearance="internal"
                  testId="threads-scope-chip"
                />
                {assistantPlatformEnabled ? (
                  <EffortToggle
                    value={effort}
                    onChange={setEffort}
                    appearance="internal"
                    className="shrink-0"
                  />
                ) : null}
                {assistantPlatformEnabled ? (
                  <WorkflowMenu
                    templates={workflowTemplates}
                    selectedSlug={armedWorkflowSlug}
                    onSelect={handleSelectLobbyWorkflow}
                    appearance="internal"
                    className="shrink-0"
                  />
                ) : null}
                {assistantPlatformEnabled ? (
                  <ContextRing
                    contextUsage={null}
                    truncated={false}
                    effort={effort}
                    onContinueInNewThread={() => undefined}
                    className="shrink-0"
                  />
                ) : null}
                <button
                  type="button"
                  data-testid="threads-attach"
                  disabled={attaching || !canLoadThreads}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-muted-foreground hover:bg-muted/70 hover:text-foreground disabled:opacity-50"
                  onClick={() => attachInputRef.current?.click()}
                >
                  <Paperclip className="h-3 w-3" />
                  {attaching ? t('threads.attach.uploading') : t('threads.attach.button')}
                </button>
              </div>
            }
          />
          {assistantPlatformEnabled ? (
            <ExamplePromptChips
              className="mt-3"
              language={contentLanguage}
              onSelect={handleExamplePromptSelect}
            />
          ) : null}
        </div>
        )}

        <PageContent className="gap-0">
          {isThreadsLocked ? (
            <div data-testid="threads-locked-state">
              <LobbyEmptyState
                title={t('threads.locked.title')}
                description={t('threads.locked.body')}
              >
                <Button asChild variant="primary">
                  <a
                    href={PYLON_CONTACT_SUPPORT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="threads-locked-contact-support"
                  >
                    {t('home.locked.contactSupport')}
                  </a>
                </Button>
              </LobbyEmptyState>
            </div>
          ) : showEmptyState ? (
            <div data-testid="threads-empty">
              <LobbyEmptyState
                title={t('threads.empty.title')}
                description={t('threads.empty.description')}
              />
            </div>
          ) : (
            <>
              <h3 className="font-display text-[18px] leading-snug text-foreground pt-1 pb-2">
                {t('threads.recentThreads')}
              </h3>

              {loading ? (
                <div
                  className="border-t border-border"
                  data-testid="threads-loading"
                >
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 border-b border-border px-4 py-3.5"
                    >
                      <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
                      <div className="min-w-0 flex-1 space-y-2">
                        <Skeleton className="h-4 w-64" />
                        <Skeleton className="h-3 w-full max-w-md" />
                      </div>
                      <div className="shrink-0 space-y-2">
                        <Skeleton className="h-3 w-16" />
                        <Skeleton className="h-3 w-12" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : visibleThreads.length > 0 ? (
                <div
                  className="border-t border-border [&>*:last-child]:border-b-0"
                  data-testid="threads-list"
                >
                  {visibleThreads.map((thread) => (
                    <ThreadCard
                      key={thread.token}
                      thread={thread}
                      onClick={() => navigate(`/threads/${thread.token}`)}
                      onRename={handleRenameThread}
                      onPin={handlePinThread}
                      onDelete={(token) => void handleDeleteThread(token)}
                    />
                  ))}
                </div>
              ) : null}

              {showNoResults && (
                <div className="py-12 text-center" data-testid="threads-no-results">
                  <Text color="muted">
                    {t('threads.noResults', { query: trimmedQuery })}
                  </Text>
                </div>
              )}
            </>
          )}
        </PageContent>

        <KnowledgeScopePicker
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          value={scope}
          onApply={setScope}
        />
      </PageLayout>
    </div>
  )
}
